import React, { useState, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { AIGeneratedPlaybook, GapDetectionResult, GapItem } from '../../types/playbook.types';

interface AIAssistantPanelProps {
  playbookId: string;
  onPreviewGenerated: (generated: AIGeneratedPlaybook) => void;
  onGapsDetected: (result: GapDetectionResult) => void;
  onClose: () => void;
}

type AITab = 'generate' | 'gaps';

const SEVERITY_STYLES: Record<string, string> = {
  high: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300',
  medium: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  low: 'border-gray-300 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const GAP_TYPE_LABELS: Record<string, string> = {
  missing_guard: 'Missing Guard',
  missing_validator: 'Missing Validator',
  dead_end_status: 'Dead-End Status',
  unreachable_status: 'Unreachable Status',
  missing_notification: 'No Notification',
  no_sla: 'No SLA',
  no_rejection_handler: 'No Rejection Handler',
};

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({ playbookId, onPreviewGenerated, onGapsDetected, onClose }) => {
  const [tab, setTab] = useState<AITab>('generate');
  const [prompt, setPrompt] = useState('');
  const [practiceArea, setPracticeArea] = useState('');
  const [jurisdictionCode, setJurisdictionCode] = useState('');
  const [complexity, setComplexity] = useState<'simple' | 'standard' | 'complex'>('standard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AIGeneratedPlaybook | null>(null);
  const [persisting, setPersisting] = useState(false);
  const [gapResult, setGapResult] = useState<GapDetectionResult | null>(null);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const resp = await playbookApi.generatePlaybook({
        prompt: prompt.trim(),
        practiceArea: practiceArea.trim() || undefined,
        jurisdictionCode: jurisdictionCode.trim() || undefined,
        complexity,
      });
      const data = (resp as any)?.playbook ?? (resp as any)?.data?.playbook;
      if (data) {
        setPreview(data);
        onPreviewGenerated(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setLoading(false);
    }
  }, [prompt, practiceArea, jurisdictionCode, complexity, onPreviewGenerated]);

  const handlePersist = useCallback(async () => {
    if (!preview) return;
    setPersisting(true);
    setError(null);
    try {
      await playbookApi.persistGeneratedPlaybook(preview);
      setPreview(null);
      setPrompt('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save playbook');
    } finally {
      setPersisting(false);
    }
  }, [preview]);

  const handleDetectGaps = useCallback(async () => {
    setLoading(true);
    setError(null);
    setGapResult(null);
    try {
      const resp = await playbookApi.detectGaps(playbookId);
      const data = (resp as any)?.gaps ? resp as any : (resp as any)?.data;
      const result: GapDetectionResult = {
        gaps: data?.gaps ?? [],
        overallScore: data?.overallScore ?? 0,
        analysisMetadata: data?.analysisMetadata ?? { model: '', analysisTimestamp: '' },
      };
      setGapResult(result);
      onGapsDetected(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gap detection failed');
    } finally {
      setLoading(false);
    }
  }, [playbookId, onGapsDetected]);

  const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
  const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';

  return (
    <div className="border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 w-80 flex flex-col" style={{ animation: 'fadeSlideLeft 200ms ease-out' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <h4 className="text-xs font-semibold text-violet-700 dark:text-violet-400">AI Assistant</h4>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm">×</button>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {(['generate', 'gaps'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`flex-1 py-1.5 text-[10px] font-medium transition-colors ${
              tab === t
                ? 'border-b-2 border-violet-500 text-violet-600 dark:text-violet-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {t === 'generate' ? 'Generate' : 'Gap Analysis'}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-3 mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[9px] text-rose-600 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {tab === 'generate' && (
          <>
            <div>
              <label className={labelClass}>Describe your playbook</label>
              <textarea
                className={`${inputClass} h-20 resize-none`}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Create a family law dissolution playbook for California with filing, service, discovery, and trial phases..."
              />
            </div>
            <div>
              <label className={labelClass}>Practice Area</label>
              <input type="text" className={inputClass} value={practiceArea} onChange={(e) => setPracticeArea(e.target.value)} placeholder="e.g. Family Law" />
            </div>
            <div>
              <label className={labelClass}>Jurisdiction</label>
              <input type="text" className={inputClass} value={jurisdictionCode} onChange={(e) => setJurisdictionCode(e.target.value)} placeholder="e.g. CA-06073" />
            </div>
            <div>
              <label className={labelClass}>Complexity</label>
              <select className={inputClass} value={complexity} onChange={(e) => setComplexity(e.target.value as typeof complexity)}>
                <option value="simple">Simple (3-5 statuses)</option>
                <option value="standard">Standard (5-10 statuses)</option>
                <option value="complex">Complex (10+ statuses)</option>
              </select>
            </div>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading || !prompt.trim()}
              className="w-full rounded bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />}
              {loading ? 'Generating...' : 'Generate Playbook'}
            </button>

            {/* Preview */}
            {preview && (
              <div className="space-y-2 rounded-lg border border-violet-200 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-900/10 p-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase">Preview</span>
                  <span className="text-[8px] text-gray-400">
                    {Math.round(preview.metadata.confidence * 100)}% confidence
                  </span>
                </div>
                <div className="text-[10px] text-gray-700 dark:text-gray-300 font-medium">{preview.name}</div>
                <div className="text-[9px] text-gray-500 dark:text-gray-400">{preview.description}</div>
                <div className="flex gap-2 text-[9px] text-gray-500">
                  <span className="text-emerald-600">{preview.statuses.length} statuses</span>
                  <span className="text-lex-accent">{preview.transitions.length} transitions</span>
                </div>

                {/* Status list */}
                <div className="space-y-0.5">
                  {preview.statuses.map((s) => (
                    <div key={s.slug} className="flex items-center gap-1 text-[9px]">
                      <span className={`h-1.5 w-1.5 rounded-full ${
                        s.category === 'done' ? 'bg-emerald-500' :
                        s.category === 'in_progress' ? 'bg-lex-accent' :
                        s.category === 'blocked' ? 'bg-rose-500' : 'bg-gray-400'
                      }`} />
                      <span className="text-gray-600 dark:text-gray-400">{s.name}</span>
                      {s.is_initial && <span className="text-[7px] text-lex-accent font-medium">START</span>}
                      {s.is_terminal && <span className="text-[7px] text-emerald-500 font-medium">END</span>}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handlePersist}
                  disabled={persisting}
                  className="w-full rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {persisting && <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                  {persisting ? 'Saving...' : 'Save as New Playbook'}
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'gaps' && (
          <>
            <p className="text-[9px] text-gray-500 dark:text-gray-400">
              Analyze this playbook for missing guards, unreachable statuses, and practice-area-specific issues.
            </p>
            <button
              type="button"
              onClick={handleDetectGaps}
              disabled={loading}
              className="w-full rounded bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {loading && <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />}
              {loading ? 'Analyzing...' : 'Run Gap Detection'}
            </button>

            {gapResult && (
              <div className="space-y-2">
                {/* Score */}
                <div className="flex items-center justify-between rounded-lg border border-violet-200 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-900/10 px-2 py-1.5">
                  <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase">Score</span>
                  <span className={`text-sm font-bold ${
                    gapResult.overallScore >= 80 ? 'text-emerald-600' :
                    gapResult.overallScore >= 50 ? 'text-amber-600' : 'text-rose-600'
                  }`}>
                    {gapResult.overallScore}/100
                  </span>
                </div>

                {gapResult.gaps.length === 0 && (
                  <p className="text-[9px] text-emerald-600 dark:text-emerald-400 text-center py-2">
                    No gaps detected — playbook looks complete.
                  </p>
                )}

                {/* Gap list */}
                {gapResult.gaps.map((gap: GapItem, i: number) => (
                  <div key={i} className={`rounded border p-2 space-y-0.5 ${SEVERITY_STYLES[gap.severity] ?? SEVERITY_STYLES.low}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-medium">{GAP_TYPE_LABELS[gap.type] ?? gap.type}</span>
                      <span className="text-[7px] uppercase font-bold opacity-70">{gap.severity}</span>
                    </div>
                    <p className="text-[8px] opacity-80">{gap.description}</p>
                    <p className="text-[8px] opacity-60">
                      {gap.affectedEntity.type}: {gap.affectedEntity.name}
                    </p>
                    {gap.suggestedFix && (
                      <div className="text-[8px] opacity-70 italic">
                        Fix: {gap.suggestedFix.action.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AIAssistantPanel;
